import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { runAfterInteractions } from '../utils/afterInteractions';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { pickImageFromLibrary } from '../utils/permissions';
import TonyteCropModal from '../components/TonyteCropModal';
import PickerField from '../components/PickerField';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { RootStackParamList, TonyteUser } from '../types';
import { auth, firestore, FIRESTORE_COLLECTIONS, uploadUserImage } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import TonyteBackground from '../components/TonyteBackground';
import { buildCompletionPatch, computeCompletionScore, getMissingProfileCompletionFields } from '../utils/profileCompletion';
import { INTEREST_CATEGORIES } from '../data/interests';
import { useI18n } from '../i18n';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import { isDateOfBirthValid } from '../utils/age';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type CompletionStep = 'profile_photo' | 'date_of_birth' | 'interests' | 'location' | 'bio' | 'cover' | 'social_vibe' | 'music' | 'languages' | 'going_out' | 'done';

const SOCIAL_VIBES = [
  { value: 'social_butterfly', labelKey: 'completeProfile.socialVibeButterfly', icon: 'users', descKey: 'completeProfile.socialVibeButterflyDesc' },
  { value: 'adventurer', labelKey: 'completeProfile.socialVibeAdventurer', icon: 'compass', descKey: 'completeProfile.socialVibeAdventurerDesc' },
  { value: 'foodie', labelKey: 'completeProfile.socialVibeFoodie', icon: 'coffee', descKey: 'completeProfile.socialVibeFoodieDesc' },
  { value: 'nightlife', labelKey: 'completeProfile.socialVibeNightlife', icon: 'moon', descKey: 'completeProfile.socialVibeNightlifeDesc' },
  { value: 'culture_hunter', labelKey: 'completeProfile.socialVibeCulture', icon: 'book-open', descKey: 'completeProfile.socialVibeCultureDesc' },
  { value: 'chill', labelKey: 'completeProfile.socialVibeChill', icon: 'sun', descKey: 'completeProfile.socialVibeChillDesc' },
];

const MUSIC_GENRES = [
  ['Techno', 'techno'], ['House', 'house'], ['Hip Hop', 'hipHop'], ['R&B', 'rAndB'], ['Pop', 'pop'],
  ['Rock', 'rock'], ['Indie', 'indie'], ['EDM', 'edm'], ['Jazz', 'jazz'], ['Classical', 'classical'],
  ['Reggaeton', 'reggaeton'], ['Afrobeats', 'afrobeats'], ['Lo-fi', 'loFi'], ['Drum & Bass', 'drumBass'], ['Ambient', 'ambient'],
] as const;

const LANGUAGE_OPTIONS = [
  ['English', 'english'], ['German', 'german'], ['French', 'french'], ['Spanish', 'spanish'],
  ['Italian', 'italian'], ['Portuguese', 'portuguese'], ['Dutch', 'dutch'], ['Polish', 'polish'],
  ['Czech', 'czech'], ['Romanian', 'romanian'], ['Greek', 'greek'], ['Hungarian', 'hungarian'],
  ['Swedish', 'swedish'], ['Danish', 'danish'], ['Finnish', 'finnish'], ['Hindi', 'hindi'],
  ['Ukrainian', 'ukrainian'], ['Russian', 'russian'], ['Arabic', 'arabic'], ['Chinese', 'chinese'], ['Japanese', 'japanese'],
] as const;

const GOING_OUT_OPTIONS = [
  { value: 'clubs', labelKey: 'completeProfile.goingOutClubs', icon: 'headphones' },
  { value: 'bars', labelKey: 'completeProfile.goingOutBars', icon: 'coffee' },
  { value: 'rooftops', labelKey: 'completeProfile.goingOutRooftops', icon: 'sunset' },
  { value: 'live_music', labelKey: 'completeProfile.goingOutLiveMusic', icon: 'music' },
  { value: 'chill_spots', labelKey: 'completeProfile.goingOutChill', icon: 'sun' },
  { value: 'street_food', labelKey: 'completeProfile.goingOutStreetFood', icon: 'shopping-bag' },
];

const ALL_STEPS: CompletionStep[] = ['profile_photo', 'interests', 'location', 'bio', 'cover', 'social_vibe', 'music', 'languages', 'going_out', 'done'];

// ── Screen ──────────────────────────────────────────────────────────────────

// react-native-web's ScrollView doesn't reliably resolve a real scrollable
// height inside this screen's flex:1 ancestor chain (TonyteBackground ->
// navigator screen container), the same class of bug already fixed for
// OnboardingThemeSelection.web.tsx and ProfileSetupScreen.tsx's SetupScroller
// — a plain View pinned to its parent with position:absolute (an explicit,
// definite box for overflow-y:auto to scroll within) works where flex:1 +
// overflow-y:auto alone silently doesn't. Native is untouched.
const Scroller = Platform.OS === 'web' ? View : ScrollView;

const CompleteProfileScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'CompleteProfile'>>();
  const editAll = route.params?.editAll ?? false;
  const initialStep = route.params?.initialStep;
  const returnAfterProfilePhoto = route.params?.returnAfterProfilePhoto ?? false;
  const autoOpenProfilePhotoPicker = route.params?.autoOpenProfilePhotoPicker ?? false;
  const uid = auth.currentUser?.uid ?? null;
  const { profile, updateProfile } = useProfile(uid);

  const [steps, setSteps] = useState<CompletionStep[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [step, setStep] = useState<CompletionStep>('bio');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step state
  const [profilePhotoUri, setProfilePhotoUri] = useState<string | null>(null);
  const [pendingImageKind, setPendingImageKind] = useState<'profile' | 'cover'>('cover');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [bio, setBio] = useState('');
  const [coverUri, setCoverUri] = useState<string | null>(null);
  const [cropUri, setCropUri] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  const [socialVibe, setSocialVibe] = useState('');
  const [musicTaste, setMusicTaste] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [goingOutStyle, setGoingOutStyle] = useState<string[]>([]);
  const autoPickerOpenedRef = useRef(false);

  // Derive the step list once when profile first loads
  useEffect(() => {
    if (!profile || initialized) return;
    const missingSteps = getMissingProfileCompletionFields(profile);
    const missingEditableSteps = profile.dateOfBirth || profile.ageGroup || profile.age
      ? missingSteps.filter(s => s !== 'date_of_birth')
      : missingSteps;
    const fieldSteps: CompletionStep[] = initialStep === 'profile_photo'
      ? ['profile_photo', 'done']
      : editAll
        ? ALL_STEPS.filter(s => s !== 'date_of_birth')
        : [...missingEditableSteps, 'done'];
    const firstStep = fieldSteps[0] ?? 'done';
    setSteps(fieldSteps);
    setStep(firstStep);
    setInitialized(true);
  }, [profile, initialized, editAll, initialStep]);

  // Prefill form values whenever profile updates
  useEffect(() => {
    if (!profile) return;
    setDateOfBirth(profile.dateOfBirth || '');
    setSelectedInterests(profile.interests || []);
    setCity(profile.locationCity || profile.city || '');
    setCountry(profile.locationCountry || profile.country || '');
    setBio(profile.bio || '');
    setSocialVibe(profile.socialVibe || '');
    setMusicTaste(profile.musicTaste || []);
    setLanguages(profile.languages || []);
    setGoingOutStyle(profile.goingOutStyle || []);
  }, [profile]);

  const { score } = computeCompletionScore(profile);
  const stepIndex = steps.indexOf(step);

  const patch = useCallback(async (data: Record<string, unknown>) => {
    if (!uid) return;
    await setDoc(
      doc(firestore, FIRESTORE_COLLECTIONS.users, uid),
      { ...data, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }, [uid]);

  const goNext = () => {
    setError('');
    const next = steps[stepIndex + 1];
    if (next) setStep(next);
  };

  const saveAndNext = async (data: Record<string, unknown>) => {
    setSaving(true);
    setError('');
    try {
      await patch(data);
      await updateProfile(data as Partial<TonyteUser>);
      const updatedProfile = { ...profile, ...data } as TonyteUser;
      const { score, missing } = computeCompletionScore(updatedProfile);
      await patch(buildCompletionPatch(score, missing));
      goNext();
    } catch {
      setError(t('completeProfile.couldNotSave'));
    } finally {
      setSaving(false);
    }
  };

  const pickProfilePhoto = useCallback(async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 0.9 });
    const uri = result?.assets?.[0]?.uri;
    if (!uri) return;
    setPendingImageKind('profile');
    setCropUri(uri);
    setShowCrop(true);
  }, []);

  useEffect(() => {
    if (!initialized || initialStep !== 'profile_photo' || !autoOpenProfilePhotoPicker || autoPickerOpenedRef.current) return;
    autoPickerOpenedRef.current = true;
    const task = runAfterInteractions(() => {
      void pickProfilePhoto();
    });
    return () => task.cancel();
  }, [autoOpenProfilePhotoPicker, initialStep, initialized, pickProfilePhoto]);

  const pickCover = async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 0.8 });
    const uri = result?.assets?.[0]?.uri;
    if (!uri) return;
    console.log('[Tonyte][ImagePreview] selected image', uri);
    setPendingImageKind('cover');
    setCropUri(uri);
    setShowCrop(true);
    console.log('[Tonyte][ImagePreview] opening preview', {
      pendingImageType: 'cover',
      imagePreviewVisible: true,
    });
    return;
  };

  const persistProfilePhoto = async (uri: string) => {
    if (!uri || !uid) { goNext(); return; }
    setSaving(true);
    setError('');
    try {
      const { downloadUrl, storagePath } = await uploadUserImage(uid, uri, 'profile');
      const data = {
        photo: downloadUrl,
        profilePhoto: downloadUrl,
        profilePhotoURL: downloadUrl,
        profileImage: downloadUrl,
        photoPath: storagePath,
        profilePhotoPath: storagePath,
        profileImagePath: storagePath,
      };
      await patch(data);
      await updateProfile(data as Partial<TonyteUser>);
      const updatedProfile = { ...profile, ...data } as TonyteUser;
      const { score: updatedScore, missing } = computeCompletionScore(updatedProfile);
      await patch(buildCompletionPatch(updatedScore, missing));
      if (returnAfterProfilePhoto) {
        navigation.goBack();
      } else {
        goNext();
      }
    } catch {
      setError(t('completeProfile.couldNotUploadProfilePhoto'));
    } finally {
      setSaving(false);
    }
  };

  const handleCropDone = (croppedUri: string) => {
    console.log('[Tonyte][ImagePreview] modal closed by confirm', {
      pendingImageType: 'cover',
      uri: croppedUri,
    });
    if (pendingImageKind === 'profile') {
      setProfilePhotoUri(croppedUri);
    } else {
      setCoverUri(croppedUri);
    }
    setShowCrop(false);
    setCropUri('');
    if (pendingImageKind === 'profile' && returnAfterProfilePhoto) {
      void persistProfilePhoto(croppedUri);
    }
  };

  const cancelImagePreview = () => {
    console.log('[Tonyte][ImagePreview] modal closed by cancel', {
      pendingImageType: 'cover',
      pendingImageUri: cropUri || null,
    });
    setShowCrop(false);
    setCropUri('');
  };

  const saveCover = async () => {
    if (!coverUri || !uid) { goNext(); return; }
    setSaving(true);
    setError('');
    try {
      const { downloadUrl, storagePath } = await uploadUserImage(uid, coverUri, 'cover');
      await patch({
        coverPhoto: downloadUrl,
        coverPhotoURL: downloadUrl,
        coverPhotoPath: storagePath,
      });
      const updatedProfile = { ...profile, coverPhoto: downloadUrl } as TonyteUser;
      const { score, missing } = computeCompletionScore(updatedProfile);
      await patch(buildCompletionPatch(score, missing));
      goNext();
    } catch {
      setError(t('completeProfile.couldNotUploadCover'));
    } finally {
      setSaving(false);
    }
  };

  const saveProfilePhoto = async () => {
    if (!profilePhotoUri || !uid) { goNext(); return; }
    await persistProfilePhoto(profilePhotoUri);
  };

  const toggleList = <T extends string>(
    list: T[],
    setList: React.Dispatch<React.SetStateAction<T[]>>,
    item: T,
    max: number,
  ) => {
    setList(current =>
      current.includes(item)
        ? current.filter(v => v !== item)
        : current.length < max
          ? [...current, item]
          : current,
    );
  };

  if (!initialized) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={Colors.turquoise} size="large" />
        </View>
      </TonyteBackground>
    );
  }

  if (step === 'done') {
    const { score: finalScore } = computeCompletionScore(profile);
    return (
      <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
        <View style={[styles.doneWrap, { paddingTop: insets.top + 40, paddingBottom: insets.bottom + 40 }]}>
          <LinearGradient colors={['rgba(38,208,206,0.22)', 'rgba(38,208,206,0.06)']} style={styles.doneIconBg}>
            <Icon name="check-circle" size={48} color={Colors.turquoise} />
          </LinearGradient>
          <Text style={[styles.doneTitle, appearance.textPrimary]}>{t('completeProfile.doneTitle')}</Text>
          <Text style={styles.doneScore}>{finalScore}% {t('completeProfile.complete')}</Text>
          <Text style={[styles.doneSub, appearance.textSecondary]}>
            {t('completeProfile.doneSub')}
          </Text>
          <TouchableOpacity style={styles.doneButton} onPress={() => navigation.goBack()} activeOpacity={0.88}>
            <Text style={styles.doneButtonText}>{t('completeProfile.backToProfile')}</Text>
          </TouchableOpacity>
        </View>
      </TonyteBackground>
    );
  }

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <Scroller
        style={Platform.OS === 'web' ? styles.webScroller : styles.scroll}
        {...(Platform.OS === 'web' ? {} : {
          contentContainerStyle: [styles.content, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 30 }],
          keyboardShouldPersistTaps: 'handled' as const,
          keyboardDismissMode: 'on-drag' as const,
          nestedScrollEnabled: true,
          scrollEnabled: true,
          showsVerticalScrollIndicator: false,
        })}
      >
      <View style={Platform.OS === 'web'
        ? [styles.content, styles.webContent, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 30 }]
        : undefined}
      >
        {/* Progress */}
        <View style={styles.progressRow}>
          {steps.filter(s => s !== 'done').map((s, idx) => (
            <View
              key={s}
              style={[
                styles.progressDot,
                step === s && styles.progressDotActive,
                stepIndex > idx && styles.progressDotDone,
              ]}
            />
          ))}
        </View>

        {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={[styles.stepIcon, appearance.control]}>
                <Icon name="user-check" size={22} color={Colors.turquoise} />
              </View>
              <View>
                <Text style={[styles.kicker, appearance.textMuted]}>{t('completeProfile.profileBoost')}</Text>
                <Text style={[styles.headerSub, appearance.textSecondary]}>{t('completeProfile.enhanceExperience')}</Text>
              </View>
            </View>
            <View style={styles.scoreBadge}>
              <Text style={styles.scoreBadgeText}>{score}%</Text>
            </View>
          </View>

          {/* ── Step: Profile Photo ── */}
          {step === 'profile_photo' ? (
            <StepWrap
              title={t('completeProfile.addProfilePicture')}
              subtitle={t('completeProfile.addProfilePictureSub')}
            >
              <TouchableOpacity style={styles.profilePhotoPicker} onPress={pickProfilePhoto} activeOpacity={0.86}>
                {profilePhotoUri ? (
                  <Image source={{ uri: profilePhotoUri }} style={styles.profilePhotoPreview} resizeMode="cover" />
                ) : profile?.photo && !profile.photo.includes('randomuser') ? (
                  <Image source={{ uri: profile.photo }} style={styles.profilePhotoPreview} resizeMode="cover" />
                ) : (
                  <View style={[styles.profilePhotoPlaceholder, appearance.control]}>
                    <Icon name="camera" size={30} color={theme.colors.iconMuted} />
                  </View>
                )}
              </TouchableOpacity>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={saveProfilePhoto}>
                {profilePhotoUri ? t('completeProfile.uploadPhoto') : t('completeProfile.keepCurrent')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Date of Birth ── */}
          {step === 'date_of_birth' ? (
            <StepWrap
              title={t('completeProfile.dobTitle')}
              subtitle={t('completeProfile.dobSub')}
            >
              <PickerField
                value={dateOfBirth}
                onChange={setDateOfBirth}
                onReject={() => setError(t('completeProfile.chooseDob'))}
                placeholder={t('completeProfile.dobPlaceholder')}
                mode="date"
                maximumDate={new Date()}
              />
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn
                saving={saving}
                onPress={() => {
                  if (!dateOfBirth) { setError(t('completeProfile.chooseDob')); return; }
                  if (!isDateOfBirthValid(dateOfBirth)) { setError(t('completeProfile.underMinAge')); return; }
                  saveAndNext({ dateOfBirth });
                }}
              >
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
            </StepWrap>
          ) : null}

          {/* ── Step: Interests ── */}
          {step === 'interests' ? (
            <StepWrap
              title={t('completeProfile.interestsTitle')}
              subtitle={t('completeProfile.interestsSub')}
            >
              <View style={styles.chipGrid}>
                {INTEREST_CATEGORIES.flatMap(category => category.interests).slice(0, 50).map(interest => {
                  const selected = selectedInterests.includes(interest);
                  return (
                    <TouchableOpacity
                      key={interest}
                      style={[styles.chip, appearance.control, selected && styles.chipActive, selected && appearance.selectedControl]}
                      onPress={() => toggleList(selectedInterests, setSelectedInterests, interest, 12)}
                      activeOpacity={0.82}
                    >
                      <Text style={[styles.chipText, appearance.textSecondary, selected && styles.chipTextActive]}>{interest}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={[styles.selCount, appearance.textMuted]}>{selectedInterests.length}/12 {t('completeProfile.selected')}</Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn
                saving={saving}
                onPress={() => selectedInterests.length >= 3
                  ? saveAndNext({ interests: selectedInterests })
                  : setError(t('completeProfile.pickInterests'))}
              >
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Location ── */}
          {step === 'location' ? (
            <StepWrap
              title={t('completeProfile.locationTitle')}
              subtitle={t('completeProfile.locationSub')}
            >
              <TextInput
                style={[styles.singleInput, appearance.input, appearance.textPrimary]}
                value={city}
                onChangeText={text => { setCity(text); setError(''); }}
                placeholder={t('completeProfile.city')}
                placeholderTextColor={theme.colors.textMuted}
              />
              <TextInput
                style={[styles.singleInput, appearance.input, appearance.textPrimary]}
                value={country}
                onChangeText={setCountry}
                placeholder={t('completeProfile.country')}
                placeholderTextColor={theme.colors.textMuted}
              />
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn
                saving={saving}
                onPress={() => {
                  const safeCity = canAccessCity(normalizeCityKey(city), profile?.role) ? city.trim() : 'Dresden';
                  return city.trim()
                    ? saveAndNext({
                      city: safeCity,
                      locationCity: safeCity,
                      country: country.trim(),
                      locationCountry: country.trim(),
                      locationLabel: `Based in ${safeCity}`,
                    })
                    : setError(t('completeProfile.addBaseCity'));
                }}
              >
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Bio ── */}
          {step === 'bio' ? (
            <StepWrap
              title={t('completeProfile.bioTitle')}
              subtitle={t('completeProfile.bioSub')}
            >
              <TextInput
                style={[styles.bioInput, appearance.input, appearance.textPrimary]}
                value={bio}
                onChangeText={text => { setBio(text); setError(''); }}
                placeholder={t('completeProfile.bioPlaceholder')}
                placeholderTextColor={theme.colors.textMuted}
                multiline
                numberOfLines={5}
              />
              <Text style={[styles.charCount, appearance.textMuted]}>{bio.length}/280</Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={() => saveAndNext({ bio: bio.trim().slice(0, 280) })}>
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Cover Photo ── */}
          {step === 'cover' ? (
            <StepWrap
              title={t('completeProfile.coverTitle')}
              subtitle={t('completeProfile.coverSub')}
            >
              <TouchableOpacity style={styles.coverPicker} onPress={pickCover} activeOpacity={0.86}>
                {coverUri ? (
                  <Image source={{ uri: coverUri }} style={styles.coverImage} resizeMode="cover" />
                ) : profile?.coverPhoto && !profile.coverPhoto.includes('unsplash') ? (
                  <Image source={{ uri: profile.coverPhoto }} style={styles.coverImage} resizeMode="cover" />
                ) : (
                  <View style={[styles.coverPlaceholder, appearance.control]}>
                    <Icon name="image" size={32} color={theme.colors.iconMuted} />
                    <Text style={[styles.coverPlaceholderText, appearance.textMuted]}>{t('completeProfile.tapChooseCover')}</Text>
                  </View>
                )}
              </TouchableOpacity>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={saveCover}>
                {coverUri ? t('completeProfile.uploadCover') : t('completeProfile.keepCurrent')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Social Vibe ── */}
          {step === 'social_vibe' ? (
            <StepWrap
              title={t('completeProfile.vibeTitle')}
              subtitle={t('completeProfile.vibeSub')}
            >
              <View style={styles.vibeGrid}>
                {SOCIAL_VIBES.map(vibe => {
                  const selected = socialVibe === vibe.value;
                  return (
                    <TouchableOpacity
                      key={vibe.value}
                      style={[styles.vibeCard, appearance.card, selected && styles.vibeCardActive, selected && appearance.selectedControl]}
                      onPress={() => setSocialVibe(vibe.value)}
                      activeOpacity={0.82}
                    >
                      {selected ? (
                        <LinearGradient colors={['rgba(38,208,206,0.3)', 'rgba(38,208,206,0.08)']} style={StyleSheet.absoluteFill} />
                      ) : null}
                      <Icon name={vibe.icon} size={22} color={selected ? Colors.turquoise : theme.colors.iconMuted} />
                      <Text style={[styles.vibeLabel, appearance.textPrimary, selected && styles.vibeLabelActive]}>{t(vibe.labelKey)}</Text>
                      <Text style={[styles.vibeDesc, appearance.textMuted]}>{t(vibe.descKey)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={() => saveAndNext({ socialVibe })}>
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Music Taste ── */}
          {step === 'music' ? (
            <StepWrap
              title={t('completeProfile.musicTitle')}
              subtitle={t('completeProfile.musicSub')}
            >
              <View style={styles.chipGrid}>
                {MUSIC_GENRES.map(([value, key]) => {
                  const selected = musicTaste.includes(value);
                  return (
                    <TouchableOpacity
                      key={value}
                      style={[styles.chip, appearance.control, selected && styles.chipActive, selected && appearance.selectedControl]}
                      onPress={() => toggleList(musicTaste, setMusicTaste, value, 5)}
                      activeOpacity={0.82}
                    >
                      <Text style={[styles.chipText, appearance.textSecondary, selected && styles.chipTextActive]}>{t(`completeProfile.music_${key}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={[styles.selCount, appearance.textMuted]}>{musicTaste.length}/5 {t('completeProfile.selected')}</Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={() => saveAndNext({ musicTaste })}>
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Languages ── */}
          {step === 'languages' ? (
            <StepWrap
              title={t('completeProfile.languagesTitle')}
              subtitle={t('completeProfile.languagesSub')}
            >
              <View style={styles.chipGrid}>
                {LANGUAGE_OPTIONS.map(([value, key]) => {
                  const selected = languages.includes(value);
                  return (
                    <TouchableOpacity
                      key={value}
                      style={[styles.chip, appearance.control, selected && styles.chipActive, selected && appearance.selectedControl]}
                      onPress={() => toggleList(languages, setLanguages, value, 10)}
                      activeOpacity={0.82}
                    >
                      <Text style={[styles.chipText, appearance.textSecondary, selected && styles.chipTextActive]}>{t(`completeProfile.spoken_${key}`)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={[styles.selCount, appearance.textMuted]}>{languages.length} {t('completeProfile.selected')}</Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={() => saveAndNext({ languages })}>
                {t('completeProfile.saveContinue')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

          {/* ── Step: Going-Out Style ── */}
          {step === 'going_out' ? (
            <StepWrap
              title={t('completeProfile.goingOutTitle')}
              subtitle={t('completeProfile.goingOutSub')}
            >
              <View style={styles.goingOutGrid}>
                {GOING_OUT_OPTIONS.map(opt => {
                  const selected = goingOutStyle.includes(opt.value);
                  return (
                    <TouchableOpacity
                      key={opt.value}
                      style={[styles.goingOutCard, appearance.card, selected && styles.goingOutCardActive, selected && appearance.selectedControl]}
                      onPress={() => toggleList(goingOutStyle, setGoingOutStyle, opt.value, 6)}
                      activeOpacity={0.82}
                    >
                      {selected ? (
                        <LinearGradient colors={['rgba(242,125,38,0.28)', 'rgba(242,125,38,0.07)']} style={StyleSheet.absoluteFill} />
                      ) : null}
                      <Icon name={opt.icon} size={22} color={selected ? Colors.sunset : theme.colors.iconMuted} />
                      <Text style={[styles.goingOutLabel, appearance.textSecondary, selected && styles.goingOutLabelActive]}>{t(opt.labelKey)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <Text style={[styles.selCount, appearance.textMuted]}>{goingOutStyle.length} {t('completeProfile.selected')}</Text>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <PrimaryBtn saving={saving} onPress={() => saveAndNext({ goingOutStyle })}>
                {t('completeProfile.saveFinish')}
              </PrimaryBtn>
              <SkipBtn saving={saving} onPress={goNext} label={t('completeProfile.skipStep')} />
            </StepWrap>
          ) : null}

        {/* Exit */}
        <TouchableOpacity style={styles.exitBtn} onPress={() => navigation.goBack()} activeOpacity={0.78}>
          <Text style={[styles.exitBtnText, appearance.textMuted]}>{t('completeProfile.saveExit')}</Text>
        </TouchableOpacity>
      </View>
      </Scroller>
      {showCrop && cropUri ? (
        <TonyteCropModal
          session={{
            kind: pendingImageKind,
            uri: cropUri,
            aspectRatio: pendingImageKind === 'profile' ? '1:1' : '8:5',
          }}
          onCrop={handleCropDone}
          onCancel={cancelImagePreview}
        />
      ) : null}
    </TonyteBackground>
  );
};

// ── Helper sub-components ────────────────────────────────────────────────────

const StepWrap: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({ title, subtitle, children }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={styles.stepBody}>
      <View style={styles.titleBlock}>
        <Text style={[styles.title, appearance.textPrimary]}>{title}</Text>
        <Text style={[styles.subtitle, appearance.textSecondary]}>{subtitle}</Text>
      </View>
      {children}
    </View>
  );
};

const PrimaryBtn: React.FC<{ saving: boolean; onPress: () => void; children: string }> = ({ saving, onPress, children }) => (
  <TouchableOpacity style={styles.orangeButton} onPress={onPress} disabled={saving} activeOpacity={0.88}>
    {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.orangeButtonText}>{children}</Text>}
  </TouchableOpacity>
);

const SkipBtn: React.FC<{ saving: boolean; onPress: () => void; label: string }> = ({ saving, onPress, label }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <TouchableOpacity style={styles.skipBtn} onPress={onPress} disabled={saving} activeOpacity={0.78}>
      <Text style={[styles.skipBtnText, appearance.textMuted]}>{label}</Text>
    </TouchableOpacity>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, minHeight: 0 },
  // flex:1 + overflow-y:auto alone doesn't reliably resolve to a real
  // scrollable height on react-native-web inside this screen's flex
  // ancestor chain — position:absolute pins this to TonyteBackground's
  // actual rendered box, giving overflow-y:auto a definite height to
  // scroll within. Same pattern as ProfileSetupScreen's webScroller.
  webScroller: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehaviorY: 'contain',
  } as any,
  webContent: { minHeight: '100%' },
  content: {
    flexGrow: 1,
    minHeight: '100%',
    justifyContent: 'flex-start',
    paddingHorizontal: 20,
    gap: 16,
  },
  progressRow: {
    flexDirection: 'row',
    alignSelf: 'center',
    gap: 8,
    marginBottom: 4,
  },
  progressDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  progressDotActive: {
    width: 36,
    backgroundColor: Colors.sunset,
    shadowColor: Colors.sunset,
    shadowOpacity: 0.38,
    shadowRadius: 18,
  },
  progressDotDone: {
    backgroundColor: 'rgba(38,208,206,0.7)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stepIcon: {
    width: 48,
    height: 48,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kicker: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.2,
  },
  headerSub: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  scoreBadge: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.3)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  scoreBadgeText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  stepBody: { gap: 18 },
  titleBlock: { alignItems: 'center', gap: 8 },
  title: {
    color: Colors.textPrimary,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  error: {
    borderRadius: 16,
    backgroundColor: 'rgba(242,125,38,0.12)',
    color: Colors.sunset,
    paddingHorizontal: 13,
    paddingVertical: 10,
    fontSize: Typography.sm,
    lineHeight: 19,
  },
  orangeButton: {
    minHeight: 54,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Colors.sunset,
    shadowOpacity: 0.24,
    shadowRadius: 18,
  },
  orangeButtonText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  skipBtn: {
    alignItems: 'center',
    paddingVertical: 10,
  },
  skipBtnText: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  // Bio
  singleInput: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: Colors.textPrimary,
    fontSize: Typography.base,
  },
  bioInput: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    minHeight: 120,
    textAlignVertical: 'top',
  },
  charCount: {
    textAlign: 'right',
    color: 'rgba(255,255,255,0.3)',
    fontSize: Typography.xs,
    marginTop: -12,
  },
  // Profile photo
  profilePhotoPicker: {
    alignSelf: 'center',
    width: 132,
    height: 132,
    borderRadius: 66,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.28)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profilePhotoPreview: {
    width: '100%',
    height: '100%',
  },
  profilePhotoPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Cover
  coverPicker: {
    height: 180,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  coverImage: { width: '100%', height: '100%' },
  coverPlaceholder: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  coverPlaceholderText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.sm,
  },
  // Social vibe
  vibeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  vibeCard: {
    width: '47.5%',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 14,
    gap: 6,
    overflow: 'hidden',
  },
  vibeCardActive: {
    borderColor: 'rgba(38,208,206,0.35)',
  },
  vibeLabel: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  vibeLabelActive: {
    color: Colors.turquoise,
  },
  vibeDesc: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: 11,
    lineHeight: 16,
  },
  // Chips
  chipGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 9,
  },
  chip: {
    minHeight: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 14,
    justifyContent: 'center',
  },
  chipActive: {
    borderColor: 'rgba(38,208,206,0.4)',
    backgroundColor: 'rgba(38,208,206,0.16)',
  },
  chipText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  chipTextActive: {
    color: Colors.turquoise,
    fontWeight: Typography.bold,
  },
  selCount: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.xs,
    textAlign: 'right',
  },
  // Going out
  goingOutGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  goingOutCard: {
    width: '47.5%',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 14,
    gap: 8,
    overflow: 'hidden',
    alignItems: 'center',
  },
  goingOutCardActive: {
    borderColor: 'rgba(242,125,38,0.35)',
  },
  goingOutLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  goingOutLabelActive: {
    color: Colors.sunset,
  },
  // Exit
  exitBtn: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  exitBtnText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  // Done screen
  doneWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 16,
  },
  doneIconBg: {
    width: 96,
    height: 96,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    ...Shadow.strong,
  },
  doneTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  doneScore: {
    color: Colors.turquoise,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
  },
  doneSub: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 22,
    textAlign: 'center',
  },
  doneButton: {
    marginTop: 8,
    minHeight: 54,
    minWidth: 200,
    borderRadius: 20,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    ...Shadow.card,
  },
  doneButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: 'transparent' }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
  }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  selectedControl: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accentSecondary,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
});

export default CompleteProfileScreen;
